import React, { useState } from "react";
import moment from "moment";

import AppButton from "components/button";
import { useTranslation } from "i18n";
import { duration } from "utils/Utils";

function Overview({
  overviewData,
  state,
  travelers,
  handelReverseSubmit,
}) {
  const { locale, t } = useTranslation();
  const [openSegments, setOpenSegments] = useState({});

  const offer = overviewData?.data?.flightOffers?.[0] || state?.flight;
  const itineraries = offer?.itineraries || [];
  const dictionaries = state?.dictionaries || {};

  const getLocation = (iata) => dictionaries?.locations?.[iata];
  const getCabin = (segmentId) =>
    offer?.travelerPricings?.[0]?.fareDetailsBySegment?.find(
      (sege) => sege?.segmentId == segmentId,
    )?.cabin;
  const getAirlineLogo = (carrierCode) => {
    const logos = dictionaries?.airlineLogos?.[carrierCode];
    return logos?.[0]?.tailImage || logos?.[0]?.squareImage;
  };
  const getTraveler = (id) =>
    travelers?.find((traveler) => traveler?.id == id);
  const travelerInitials = (traveler) =>
    `${traveler?.name?.firstName?.charAt(0) || ""}${
      traveler?.name?.lastName?.charAt(0) || ""
    }`;
  const travelerShort = (traveler) =>
    traveler
      ? `${traveler.name?.firstName}.${traveler.name?.lastName?.charAt(0)}`
      : "";

  const toggleSegment = (key) => {
    setOpenSegments((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const baggageRows =
    offer?.travelerPricings?.filter(
      (sege) => sege?.fareDetailsBySegment?.[0]?.includedCheckedBags?.quantity > 0,
    ) || [];

  const seatRows =
    offer?.travelerPricings?.flatMap((item) =>
      (item?.fareDetailsBySegment || [])
        .filter((fare) => fare?.additionalServices?.chargeableSeatNumber)
        .map((fare) => ({ item, fare })),
    ) || [];

  return (
    <div className="bk-s4">
      <section className="bk-s1-trip">
        <div className="bk-s1-trip-head">
          <div>
            <p className="bk-s1-kicker">{t("booking.labels.itinerary")}</p>
            <h2>{t("booking.labels.tripSummary")}</h2>
          </div>
          <span className="bk-s1-trip-count">
            {itineraries.length}{" "}
            {itineraries.length === 1
              ? t("booking.labels.flight")
              : t("booking.labels.flights")}
          </span>
        </div>

        <div className="bk-s1-itin-list">
          {itineraries.map((item, itineraryIndex) => {
            const stopCount = Math.max((item?.segments?.length || 1) - 1, 0);

            return (
              <article className="bk-s1-itin" key={itineraryIndex}>
                <div className="bk-s1-itin-top">
                  <span className="bk-s1-itin-badge">
                    {state?.flightDetails?.return === "Multi-city" ? (
                      t("booking.labels.trip", { number: itineraryIndex + 1 })
                    ) : itineraryIndex === 0 ? (
                      t("booking.labels.departure")
                    ) : (
                      t("booking.labels.return")
                    )}
                  </span>
                  <span className="bk-s1-itin-meta">
                    {stopCount === 0
                      ? t("booking.labels.direct")
                      : t("booking.labels.stopCount", { count: stopCount })}
                    <span aria-hidden="true">·</span>
                    {t("booking.labels.duration")} {duration(item?.duration)}
                  </span>
                </div>

                {item?.segments?.map((segment, segmentIndex) => {
                  const key = `${itineraryIndex}-${segmentIndex}`;
                  const isOpen = Boolean(openSegments[key]);
                  const from = getLocation(segment?.departure?.iataCode);
                  const to = getLocation(segment?.arrival?.iataCode);
                  const logo = getAirlineLogo(segment?.carrierCode);
                  const cabin = getCabin(segment?.id);

                  return (
                    <div className="bk-s1-seg" key={key}>
                      {item.segments.length > 1 && (
                        <p className="bk-s1-seg-cities">
                          {from?.city || segment?.departure?.iataCode}
                          <span aria-hidden="true">→</span>
                          {to?.city || segment?.arrival?.iataCode}
                        </p>
                      )}

                      <div className="bk-s1-seg-route">
                        <div className="bk-s1-seg-point">
                          <strong>
                            {moment(segment?.departure?.at).format("H:mm")}
                          </strong>
                          <span>{segment?.departure?.iataCode}</span>
                          <small>
                            {moment(segment?.departure?.at)
                              .locale(locale)
                              .format("ddd D MMM")}
                          </small>
                        </div>

                        <div className="bk-s1-seg-line">
                          <span>
                            {duration(segment?.duration, item?.duration)}
                          </span>
                        </div>

                        <div className="bk-s1-seg-point is-end">
                          <strong>
                            {moment(segment?.arrival?.at).format("H:mm")}
                          </strong>
                          <span>{segment?.arrival?.iataCode}</span>
                          <small>
                            {moment(segment?.arrival?.at)
                              .locale(locale)
                              .format("ddd D MMM")}
                          </small>
                        </div>
                      </div>

                      <button
                        type="button"
                        className={`bk-s1-seg-toggle${isOpen ? " is-open" : ""}`}
                        onClick={() => toggleSegment(key)}
                      >
                        <span className="bk-s1-seg-carrier">
                          {logo ? (
                            <img className="bk-s1-airline" src={logo} alt="" />
                          ) : null}
                          {cabin && <em>{cabin}</em>}
                          <span>
                            {t("booking.labels.flightNo")} {segment?.number}
                          </span>
                        </span>
                        <span className="bk-s1-seg-more">
                          {isOpen
                            ? t("booking.labels.hideFlightDetails")
                            : t("booking.labels.showFlightDetails")}
                        </span>
                      </button>

                      {isOpen && (
                        <div className="bk-s1-seg-details">
                          <p>
                            <span>{t("booking.labels.airline")}</span>
                            {dictionaries?.carriers?.[segment?.carrierCode] ||
                              segment?.carrierCode}
                          </p>
                          <p>
                            <span>{t("booking.labels.aircraft")}</span>
                            {segment?.aircraft?.code || "—"}
                          </p>
                          <p>
                            <span>
                              {from?.airport || segment?.departure?.iataCode}
                            </span>
                            <span aria-hidden="true">→</span>
                            {to?.airport || segment?.arrival?.iataCode}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </article>
            );
          })}
        </div>
      </section>

      <section className="bk-s4-block">
        <div className="bk-s1-trip-head">
          <div>
            <p className="bk-s1-kicker">{t("booking.labels.passengers")}</p>
            <h2>{t("booking.labels.travelersInfo")}</h2>
            <p className="bk-s1-people-copy">
              {t("booking.messages.reviewHint")}
            </p>
          </div>
          <span className="bk-s1-trip-count">
            {travelers?.length || 0}{" "}
            {(travelers?.length || 0) === 1
              ? t("booking.labels.passenger")
              : t("booking.labels.passengers")}
          </span>
        </div>

        <div className="bk-s4-travelers">
          {travelers?.map((traveler) => {
            const travelerType = offer?.travelerPricings?.find(
              (trav) => trav.travelerId == traveler.id,
            )?.travelerType;

            return (
              <article className="bk-s4-person" key={traveler?.id}>
                <span className="bk-s4-avatar">{travelerInitials(traveler)}</span>
                <div className="bk-s4-person-copy">
                  <div className="bk-s4-person-top">
                    <p>
                      {traveler?.name?.firstName} {traveler?.name?.lastName}
                    </p>
                    {travelerType && (
                      <span>
                        {t(`booking.travelerTypes.${travelerType}`)}
                      </span>
                    )}
                  </div>
                  <p className="bk-s4-person-meta">
                    {traveler?.gender} · {traveler?.dateOfBirth}
                  </p>
                  {(traveler?.contact?.emailAddress ||
                    traveler?.contact?.phones?.[0]?.number) && (
                    <div className="bk-s4-person-contacts">
                      {traveler?.contact?.emailAddress && (
                        <span>{traveler.contact.emailAddress}</span>
                      )}
                      {traveler?.contact?.phones?.[0]?.number && (
                        <span>{traveler.contact.phones[0].number}</span>
                      )}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {baggageRows.length > 0 && (
        <section className="bk-s4-block">
          <p className="bk-s4-block-title">{t("booking.labels.baggage")}</p>
          <div className="bk-s4-lines">
            {baggageRows.map((sege) => {
              const traveler = getTraveler(sege?.travelerId);
              const quantity =
                sege?.fareDetailsBySegment?.[0]?.includedCheckedBags?.quantity;

              return (
                <div className="bk-s4-line" key={sege?.travelerId}>
                  <div>
                    <p>
                      {quantity}× {t("booking.labels.personalItem")}
                    </p>
                    <span>
                      {quantity} × 23 kg
                    </span>
                  </div>
                  <strong>{travelerShort(traveler)}</strong>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {seatRows.length > 0 && (
        <section className="bk-s4-block">
          <p className="bk-s4-block-title">{t("booking.labels.seating")}</p>
          <div className="bk-s4-lines">
            {seatRows.map(({ item, fare }, index) => {
              const traveler = getTraveler(item?.travelerId);
              const segment = itineraries
                .flatMap((itin) => itin?.segments || [])
                .find((seg) => seg?.id == fare?.segmentId);

              return (
                <div className="bk-s4-line" key={`${item?.travelerId}-${index}`}>
                  <div>
                    <p>
                      {segment?.departure?.iataCode || "—"} —{" "}
                      {segment?.arrival?.iataCode || "—"}
                    </p>
                    <span>{t("booking.labels.specificSeat")}</span>
                  </div>
                  <strong>
                    {travelerShort(traveler)} ·{" "}
                    {fare?.additionalServices?.chargeableSeatNumber}
                  </strong>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="bk-s1-footer">
        <div className="bk-s1-footer-copy">
          <p>{t("booking.labels.readyToConfirm")}</p>
          <span>{t("booking.messages.confirmHint")}</span>
        </div>
        <AppButton
          type="button"
          onClick={() => handelReverseSubmit()}
          className="bigBtn primary-color booking"
        >
          {t("booking.actions.placeReservation")}
          {state?.flightDetails?.return !== "One-way" && (
            <>
              {" — "}
              {t("booking.actions.days", {
                count: state?.flightDetails?.totalDays,
              })}
            </>
          )}
        </AppButton>
      </div>
    </div>
  );
}

export default Overview;
